import React, { useId, useState } from 'react';
import styled from '@emotion/styled';
import type { StaysBlock } from '../../../../interfaces/stays';
import { createBlock, deleteBlock, listListingBlocks } from '../../../../api/stays-reservations-client';
import { useResource } from '../../../hooks/use-resource';
import { AppButton } from '../../../button/app-button';
import { ConfirmDialog } from '../../../modal/confirm-dialog';
import { Field, FieldError, FieldHint, Input, Label } from '../../../form-kit';
import { up } from '../../../../style-utils/media-queries';
import { palette, radius, spacing, typography } from '../../../../style-utils/theme';
import toast from '../../../../utils/toast';
import { Card } from '../../dashboard/dashboard-stays-components/dashboard-stays.styled';
import { blockLine, shortDay } from './calendar-model';
import { DEFAULT_REASON, REASON_MAX } from './manage-dates-sheet';

/**
 * THE OWNER BLOCKS CARD (plan S4.10) — close a range by typing it, and the
 * upcoming owner blocks with "Re-open". stays:manage only (the parent does not
 * render it otherwise). Native date inputs give 'YYYY-MM-DD' building keys as
 * typed; "Until" is the morning the dates open again (not included).
 */

const Title = styled.h3`
  margin: 0;
  font-family: ${typography.ui};
  font-size: ${typography.size.md}px;
  font-weight: ${typography.weight.semibold};
  color: ${palette.ink};
`;

const FormGrid = styled.form`
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 0 ${spacing.sm};
  /* Top, not end: Reason carries a hint under its input, so bottom alignment
     lifted its input above the two dates. Every label is one line, so the
     inputs line up when the fields start together. */
  align-items: start;
  min-width: 0;

  ${up('md')} {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  ${up('lg')} {
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr) minmax(0, 2fr) auto;
  }
`;

const Submit = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  margin-bottom: ${spacing.sm};
`;

/* An empty label's height above the button, so the button lines up with the
   inputs beside it. Not needed when the form is one column. */
const LabelSpace = styled(Label)`
  display: none;
  visibility: hidden;

  ${up('md')} {
    display: block;
  }
`;

const List = styled.ul`
  display: flex;
  flex-direction: column;
  gap: ${spacing.xs};
  margin: 0;
  padding: 0;
  list-style: none;
`;

const Row = styled.li`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: ${spacing.xs};
  min-width: 0;
  padding: ${spacing.xs} ${spacing.sm};
  border: 1px solid ${palette.line};
  border-radius: ${radius.sm};
`;

const RowText = styled.span`
  flex: 1 1 ${spacing.huge};
  min-width: 0;
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  color: ${palette.ink};
  overflow-wrap: anywhere;
`;

const Quiet = styled.p`
  margin: 0;
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  color: ${palette.slate};
`;

const Alert = styled.p`
  margin: 0;
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  line-height: ${typography.leading.normal};
  color: ${palette.alertStrong};
  overflow-wrap: anywhere;
`;

const DAY_KEY = /^\d{4}-\d{2}-\d{2}$/;

export interface BlockManagerProps {
  listingId: string;
  /** The building's today: "upcoming" ends after it. */
  today: string;
  /** Bump to read the blocks again (the calendar changed them). */
  reloadKey?: number;
  /** After a close or a re-open here: the calendar reads again. */
  onChanged: () => void;
}

const errorText = (e: unknown, fallback: string): string =>
  e instanceof Error && e.message ? e.message : fallback;

export const BlockManager: React.FC<BlockManagerProps> = ({ listingId, today, reloadKey = 0, onChanged }) => {
  const [from, setFrom] = useState('');
  const [until, setUntil] = useState('');
  const [reason, setReason] = useState(DEFAULT_REASON);
  const [fieldError, setFieldError] = useState<{ from?: string; until?: string }>({});
  const [serverError, setServerError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [reopening, setReopening] = useState<StaysBlock | null>(null);
  const ids = { from: useId(), until: useId(), reason: useId(), hint: useId(), title: useId() };

  const resource = useResource(() => listListingBlocks(listingId, { from: today }), {
    deps: [listingId, today, reloadKey],
    fallbackMessage: "Couldn't load the closed dates",
  });
  const upcoming = ((resource.data && resource.data.blocks) || [])
    .filter(b => b.kind === 'owner' && b.end_date > today)
    .sort((a, b) => (a.start_date < b.start_date ? -1 : 1));

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (saving) return;
    const errors: { from?: string; until?: string } = {};
    if (!DAY_KEY.test(from)) errors.from = 'Choose the first night to close.';
    if (!DAY_KEY.test(until)) errors.until = 'Choose the day the dates open again.';
    else if (DAY_KEY.test(from) && until <= from) errors.until = 'Choose a day after the first night.';
    setFieldError(errors);
    setServerError(null);
    if (errors.from || errors.until) return;
    setSaving(true);
    try {
      const note = reason.trim();
      await createBlock(listingId, { start_date: from, end_date: until, ...(note ? { note } : {}) });
      toast.success('Dates closed.');
      setFrom('');
      setUntil('');
      setReason(DEFAULT_REASON);
      // The calendar reads again and bumps reloadKey, which reloads this list.
      onChanged();
    } catch (e) {
      setServerError(errorText(e, 'Could not close these dates.'));
    } finally {
      setSaving(false);
    }
  };

  const reopen = async () => {
    const block = reopening;
    if (!block) return;
    try {
      await deleteBlock(block.id);
      toast.success('Dates re-opened.');
      setReopening(null);
      onChanged();
    } catch (e) {
      setReopening(null);
      toast.error(errorText(e, 'Could not re-open these dates.'));
    }
  };

  return (
    <Card aria-labelledby={ids.title} data-block-manager="">
      <Title id={ids.title}>Owner blocks</Title>
      <FormGrid noValidate onSubmit={event => void submit(event)} aria-label="Close dates">
        <Field>
          <Label htmlFor={ids.from}>From</Label>
          <Input
            id={ids.from}
            type="date"
            name="from"
            value={from}
            hasError={!!fieldError.from}
            aria-invalid={fieldError.from ? true : undefined}
            onChange={event => setFrom(event.target.value)}
          />
          <FieldError message={fieldError.from} role="alert" />
        </Field>
        <Field>
          <Label htmlFor={ids.until}>Until (not included)</Label>
          <Input
            id={ids.until}
            type="date"
            name="until"
            value={until}
            min={DAY_KEY.test(from) ? from : undefined}
            hasError={!!fieldError.until}
            aria-invalid={fieldError.until ? true : undefined}
            onChange={event => setUntil(event.target.value)}
          />
          <FieldError message={fieldError.until} role="alert" />
        </Field>
        <Field>
          <Label htmlFor={ids.reason}>Reason</Label>
          <Input
            id={ids.reason}
            name="reason"
            value={reason}
            maxLength={REASON_MAX}
            aria-describedby={ids.hint}
            onChange={event => setReason(event.target.value)}
          />
          <FieldHint id={ids.hint}>Do not write guest names here</FieldHint>
        </Field>
        <Submit>
          <LabelSpace as="span" aria-hidden="true">
            &nbsp;
          </LabelSpace>
          <AppButton type="submit" loading={saving} loadingLabel="Closing dates">
            Close dates
          </AppButton>
        </Submit>
      </FormGrid>
      {serverError ? <Alert role="alert">{serverError}</Alert> : null}
      {resource.error && !resource.data ? (
        <Alert role="alert">Couldn&apos;t load the closed dates.</Alert>
      ) : upcoming.length ? (
        <List aria-label="Upcoming owner blocks">
          {upcoming.map(b => (
            <Row key={b.id} data-block-id={b.id}>
              <RowText>{blockLine(b)}</RowText>
              <AppButton
                variant="secondary"
                aria-label={`Re-open ${shortDay(b.start_date)} → ${shortDay(b.end_date)}`}
                onClick={() => setReopening(b)}
              >
                Re-open
              </AppButton>
            </Row>
          ))}
        </List>
      ) : resource.data ? (
        <Quiet>No upcoming owner blocks.</Quiet>
      ) : null}
      <ConfirmDialog
        open={!!reopening}
        title="Re-open these dates?"
        message={reopening ? `${blockLine(reopening)}. These nights can be booked again.` : ''}
        primaryLabel="Re-open"
        onPrimary={reopen}
        dismissLabel="Keep closed"
        onDismiss={() => setReopening(null)}
      />
    </Card>
  );
};
